import { useCallback, useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  BarChart,
  Bar,
  XAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie
} from 'recharts';
import { API_BASE_URL } from '../../config';

export default function SuperTenantDashboard() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const fetchSummary = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE_URL}/api/supertenant/summary-reports?superTenantEmail=${encodeURIComponent(user.tenantEmail)}`);
      setSummary(res.data);
    } catch (err) {
      console.error("fetchSummary Error:", err);
      toast.error('Failed to load combined reports');
    } finally {
      setLoading(false);
    }
  }, [user.tenantEmail]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const handleSwitchTenant = (tenant) => {
    const newUser = {
      ...user,
      impersonatedTenantEmail: tenant.tenantEmail,
      impersonatedSchoolName: tenant.schoolName,
      impersonatedLogoUrl: tenant.logoUrl
    };
    localStorage.setItem('user', JSON.stringify(newUser));
    toast.info(`Switching to ${tenant.schoolName}...`);
    window.location.href = `/${tenant.tenantEmail}/lms`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-12 h-12 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  const COLORS = ['#6366f1', '#8b5cf6', '#ec4899', '#f43f5e', '#f97316', '#eab308', '#22c55e', '#06b6d4', '#3b82f6'];

  return (
    <div className="max-w-[1600px] mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500 pb-12">
      {/* Welcome Section */}
      <div className="relative rounded-[2.5rem] bg-gradient-to-br from-indigo-900/40 via-slate-900 to-slate-950 border border-white/5 p-10 overflow-hidden shadow-2xl">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-indigo-500/10 blur-[120px] rounded-full -mr-48 -mt-48 animate-pulse"></div>
        <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-blue-500/10 blur-[100px] rounded-full -ml-24 -mb-24"></div>
        
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-8">
          <div>
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-indigo-500/20 text-indigo-300 text-xs font-bold mb-6 border border-indigo-500/30 tracking-widest uppercase">
              Super Tenant Command Center
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 tracking-tight">
              Greetings, <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-sky-400">{user.name}</span>
            </h1>
            <p className="text-slate-400 text-lg max-w-2xl leading-relaxed">
              Monitoring <span className="text-white font-semibold">{summary?.totalInstitutes}</span> active institutes with a combined population of <span className="text-white font-semibold">{summary?.totalStudents}</span> students.
            </p>
          </div>
          
          <div className="flex flex-wrap gap-4">
            <button 
              onClick={fetchSummary}
              className="p-4 rounded-2xl bg-white/5 border border-white/10 text-white hover:bg-white/10 transition-all hover:scale-105 active:scale-95"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 2v6h-6"></path><path d="M3 12a9 9 0 0 1 15-6.7L21 8"></path><path d="M3 22v-6h6"></path><path d="M21 12a9 9 0 0 1-15 6.7L3 16"></path></svg>
            </button>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { label: 'Total Institutes', value: summary?.totalInstitutes, icon: 'M19 21V5a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v16', color: 'bg-blue-500' },
          { label: 'Global Students', value: summary?.totalStudents, icon: 'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2', color: 'bg-indigo-500' },
          { label: 'Global Teachers', value: summary?.totalTeachers, icon: 'M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2', color: 'bg-emerald-500' },
          { label: 'Total active Batches', value: summary?.totalBatches, icon: 'M4 21v-7a4 4 0 0 1 4-4h8a4 4 0 0 1 4 4v7', color: 'bg-amber-500' }
        ].map((kpi, i) => (
          <div key={i} className="bg-slate-900 shadow-xl border border-white/5 rounded-[2rem] p-7 group hover:border-indigo-500/30 transition-all">
            <div className={`w-14 h-14 rounded-2xl ${kpi.color}/10 ${kpi.color.replace('bg-', 'text-')} flex items-center justify-center mb-6 shadow-inner ring-1 ring-white/5`}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d={kpi.icon}></path></svg>
            </div>
            <p className="text-slate-400 text-sm font-medium mb-1">{kpi.label}</p>
            <h3 className="text-3xl font-bold text-white tracking-tight">{kpi.value}</h3>
          </div>
        ))}
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-slate-900 border border-white/5 rounded-[2.5rem] p-8 shadow-2xl">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-xl font-bold text-white tracking-tight">Student Distrubution</h2>
            <div className="text-xs text-slate-500 font-mono uppercase tracking-widest">By Institute</div>
          </div>
          <div className="h-[350px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={summary?.tenantStats}>
                <XAxis dataKey="schoolName" axisLine={false} tickLine={false} tick={{fill: '#64748b', fontSize: 12}} />
                <Tooltip 
                  cursor={{fill: 'rgba(255,255,255,0.05)'}}
                  contentStyle={{backgroundColor: '#0f172a', border: '1px solid #1e293b', borderRadius: '16px', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)'}}
                />
                <Bar dataKey="studentCount" radius={[10, 10, 0, 0]}>
                  {summary?.tenantStats?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-slate-900 border border-white/5 rounded-[2.5rem] p-8 shadow-2xl">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-xl font-bold text-white tracking-tight">Market Share</h2>
            <div className="text-xs text-slate-500 font-mono uppercase tracking-widest">Enrolled Population</div>
          </div>
          <div className="h-[350px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={summary?.tenantStats}
                  cx="50%"
                  cy="50%"
                  innerRadius={80}
                  outerRadius={120}
                  paddingAngle={8}
                  dataKey="studentCount"
                  nameKey="schoolName"
                >
                  {summary?.tenantStats?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} stroke="none" />
                  ))}
                </Pie>
                <Tooltip 
                   contentStyle={{backgroundColor: '#0f172a', border: '1px solid #1e293b', borderRadius: '16px'}}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Managed Institutes Table */}
      <div className="bg-slate-900 border border-white/5 rounded-[2.5rem] p-8 shadow-2xl overflow-hidden">
        <h2 className="text-xl font-bold text-white mb-8 tracking-tight">Managed Institutes Fleet</h2>
        
        <div className="overflow-x-auto">
          <table className="w-full text-left border-separate border-spacing-y-4">
            <thead>
              <tr className="text-slate-500 text-sm font-medium">
                <th className="px-6 pb-2">Institution</th>
                <th className="px-6 pb-2">Public ID (Email)</th>
                <th className="px-6 pb-2">Population</th>
                <th className="px-6 pb-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {summary?.tenantStats?.map((tenant) => (
                <tr key={tenant.tenantEmail} className="group transition-all">
                  <td className="px-6 py-5 bg-white/5 border-y border-l border-white/5 group-hover:border-indigo-500/30 rounded-l-[1.5rem] transition-all">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-slate-800 border border-white/10 flex items-center justify-center p-2 overflow-hidden shadow-inner font-bold text-indigo-400">
                        {tenant.logoUrl ? (
                          <img src={tenant.logoUrl} alt="" className="w-full h-full object-contain" />
                        ) : (
                          tenant.schoolName?.substring(0, 2).toUpperCase()
                        )}
                      </div>
                      <div className="font-bold text-white">{tenant.schoolName}</div>
                    </div>
                  </td>
                  <td className="px-6 py-5 bg-white/5 border-y border-white/5 group-hover:border-indigo-500/30 transition-all">
                    <span className="text-slate-400 font-mono text-sm">{tenant.tenantEmail}</span>
                  </td>
                  <td className="px-6 py-5 bg-white/5 border-y border-white/5 group-hover:border-indigo-500/30 transition-all">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-indigo-500"></div>
                      <span className="text-white font-semibold">{tenant.studentCount}</span>
                      <span className="text-slate-500 text-xs uppercase tracking-tighter ml-1">Students</span>
                    </div>
                  </td>
                  <td className="px-6 py-5 bg-white/5 border-y border-r border-white/5 group-hover:border-indigo-500/30 rounded-r-[1.5rem] text-right transition-all">
                    <button 
                      onClick={() => handleSwitchTenant(tenant)}
                      className="px-6 py-2.5 rounded-xl bg-indigo-500 hover:bg-indigo-600 text-white text-sm font-bold shadow-lg shadow-indigo-900/40 transition-all hover:scale-105 active:scale-95 flex items-center gap-2 ml-auto"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"></path><polyline points="10 17 15 12 10 7"></polyline><line x1="15" y1="12" x2="3" y2="12"></line></svg>
                      Manage Instance
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
